import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { qty } from '../lib/format.js';

/** Every word typed must appear somewhere in the name, in any order. */
const matches = (item, words) => {
  const name = item.name.toLowerCase();
  return words.every((w) => name.includes(w));
};

/**
 * A type-to-filter item chooser. The list is fixed to the viewport so a table
 * that scrolls sideways does not clip it.
 */
export default function ItemPicker({ items, value, onChange, godownName }) {
  const chosen = items.find((i) => i.id === value) || null;
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [box, setBox] = useState(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const shown = useMemo(() => {
    const words = text.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return words.length ? items.filter((i) => matches(i, words)) : items;
  }, [items, text]);

  useEffect(() => { setActive(0); }, [text]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const r = inputRef.current.getBoundingClientRect();
      const below = window.innerHeight - r.bottom - 8;
      const up = below < 220 && r.top > below;
      setBox({
        left: r.left, width: Math.max(r.width, 320),
        ...(up ? { bottom: window.innerHeight - r.top + 4, maxHeight: Math.min(320, r.top - 8) }
          : { top: r.bottom + 4, maxHeight: Math.min(320, below) }),
      });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => { window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true); };
  }, [open]);

  useEffect(() => {
    const el = listRef.current && listRef.current.children[active];
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  const pick = (item) => {
    onChange(item);
    setText('');
    setOpen(false);
  };

  const onKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setActive((a) => Math.min(a + 1, shown.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter') { if (open && shown[active]) { e.preventDefault(); pick(shown[active]); } }
    else if (e.key === 'Escape') { setOpen(false); setText(''); }
  };

  return (
    <div className="item-picker">
      <input ref={inputRef} type="text" role="combobox" aria-expanded={open} aria-autocomplete="list"
        value={open ? text : (chosen ? chosen.name : '')}
        placeholder={chosen ? chosen.name : `Type to search ${items.length} items in stock…`}
        onFocus={() => setOpen(true)}
        onBlur={() => { setOpen(false); setText(''); }}
        onChange={(e) => { setText(e.target.value); setOpen(true); }}
        onKeyDown={onKey} />
      {open && box && (
        <ul ref={listRef} className="item-picker-list" role="listbox" style={{ position: 'fixed', ...box }}>
          {shown.length ? shown.map((i, idx) => (
            <li key={i.id} role="option" aria-selected={i.id === value}
              className={idx === active ? 'active' : undefined}
              onMouseEnter={() => setActive(idx)}
              // mousedown, not click: the input's blur would close the list first.
              onMouseDown={(e) => { e.preventDefault(); pick(i); }}>
              <span className="name">{i.name}</span>
              <span className="stock num">
                {qty(i.qty_available, i.uom)}
                {godownName && <span className="here">{qty(i.qty_godown, i.uom)} in {godownName}</span>}
              </span>
            </li>
          )) : <li className="none">No item in stock matches “{text.trim()}”</li>}
        </ul>
      )}
    </div>
  );
}
